import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { openModal } from '../../components/modal.js';
import { confirmDialog } from '../../components/confirm-dialog.js';
import { toast } from '../../components/toast.js';
import { readMeta, sourceCanWrite } from './catalogue.js';
import { cxMark } from './mark.js';
import { cxBtn } from './ui.js';
import { renderFields, readFields, textField } from './fields.js';
import { SCHEMAS, collectCredential, githubMeta, presentCredential } from './schemas.js';
import { syncDocsPath } from '../../core/parity.js';
import { renderGuideColumn, unlockLine, createHandshake } from './guide-panel.js';

/** Keep aria-label as a string; inject the brand mark into the modal h2 (modal.js stays untouched). */
function titleWithMark(titleText, slug, displayName) {
  requestAnimationFrame(() => {
    const panels = document.querySelectorAll('.modal.cx-modal');
    const panel = panels[panels.length - 1];
    const heading = panel && panel.querySelector('.drawer-head h2');
    if (!heading) return;
    heading.classList.add('cx-modal-title');
    heading.replaceChildren(cxMark(slug, displayName || titleText), document.createTextNode(titleText));
  });
}

const WRITE_COPY = 'This source can write — Bonnie only writes when you ask';

function writeNote(item) {
  if (!sourceCanWrite(item)) return null;
  return el('p', { class: 'cx-write' }, WRITE_COPY);
}

function errorLine() {
  return el('p', { class: 'cx-form-error', role: 'alert' });
}

function fail(node, result, fallback) {
  const status = result && result.status;
  const unreachable = status === 502 || status === 504;
  const heading = unreachable ? 'Couldn’t reach the provider. Nothing was saved.' : (result && result.error) || fallback;
  node.textContent = heading;
}

async function loadHints(slug, id) {
  const cred = await api(`/connectors/${encodeURIComponent(id)}/credential`);
  if (!cred || cred.error) return { values: {}, hints: {} };
  return presentCredential(slug, cred.value);
}

function nativeBody(item, mode, preset, instance) {
  const editing = mode === 'manage';
  const schema = SCHEMAS[item.name];
  const meta = readMeta(instance && instance.metadata);
  const fields = renderFields(schema, item.name, preset);
  const label = textField('label', editing ? 'Label' : 'Label (optional)', (instance && instance.label) || '', 'e.g. Production');
  const repo = item.name === 'github'
    ? el('div', { class: 'cx-fields' },
      textField('repo', 'Repository (required)', meta.repo || '', 'owner/repo'),
      textField('branch', 'Branch (optional)', meta.branch || '', 'main, staging, release/…'))
    : null;
  const another = mode === 'another'
    ? el('p', { class: 'cx-hint' }, `Adding another ${item.title}. Use a clear label${item.name === 'github' ? ' and a different repository' : ''}.`)
    : null;
  return { fields, label, repo, another, editing };
}

async function saveNative(item, mode, instance, root, error, close, reload) {
  const editing = mode === 'manage';
  const values = readFields(root);
  const built = collectCredential(item.name, values, editing);
  if (built.error) { error.textContent = built.error; return; }
  let metadata;
  if (item.name === 'github') {
    const repo = githubMeta(values, editing);
    if (repo.error) { error.textContent = repo.error; return; }
    metadata = repo.metadata;
  }
  const payload = { label: values.label || '' };
  if (!built.keep) payload.value = built.value;
  if (metadata) payload.metadata = metadata;
  if (!editing) {
    payload.product_id = item.productId;
    payload.provider_id = item.id;
    payload.credential_type = 'api_key';
    if (!payload.value) { error.textContent = 'Credential value is required'; return; }
  }
  const path = editing ? `/connectors/${encodeURIComponent(instance.id)}` : '/connectors';
  const method = editing ? 'PUT' : 'POST';
  const result = await api(path, { method, body: payload });
  if (!result || result.error || (editing && !result.integration)) {
    fail(error, result, 'Could not save this connector');
    return;
  }
  close();
  const ms = result.latency_ms;
  toast(ms != null ? `Connected · ${ms} ms` : (editing ? 'Connector updated' : 'Connector connected'));
  if (item.name === 'github') {
    const savedId = (result.integration && result.integration.id) || (instance && instance.id);
    if (savedId) api(syncDocsPath(savedId), { method: 'POST', body: {} }).then(() => {});
  }
  if (reload) reload();
}

function submitRow(label, onClick, extra) {
  return el('div', { class: 'cx-book-foot' },
    extra || null,
    cxBtn(label, { className: 'cx-cta', onClick }));
}

function wireHandshake(scroll, handshake) {
  const refresh = () => handshake.updateFromForm(scroll);
  scroll.addEventListener('input', refresh);
  scroll.addEventListener('change', refresh);
  refresh();
}

async function openNativeForm(item, mode, instance, opts, reload) {
  const preset = mode === 'manage' ? await loadHints(item.name, instance.id) : { values: {}, hints: {} };
  const parts = nativeBody(item, mode, preset, instance);
  const error = errorLine();
  const handshake = createHandshake(item.name);
  const formCol = el('div', { class: 'cx-form-col' },
    writeNote(item),
    parts.another,
    error,
    parts.label,
    parts.repo,
    parts.fields);
  const scroll = el('div', { class: 'cx-connect-scroll' },
    unlockLine(item.name),
    el('div', { class: 'cx-connect-grid' },
      renderGuideColumn(item.name, item.title),
      formCol),
    handshake.root);
  const body = el('div', { class: 'cx-book cx-connect' }, scroll);
  const title = mode === 'manage' ? `Manage ${item.title}` : `Connect ${item.title}`;
  const modal = openModal({ title, body, className: 'cx-modal cx-modal-wide' });
  titleWithMark(title, item.name, item.title);
  const disconnect = mode === 'manage'
    ? cxBtn('Disconnect', { onClick: () => removeNative(instance, item, modal.close, reload) })
    : null;
  body.append(submitRow(mode === 'manage' ? 'Save & verify' : 'Test & connect', async (event) => {
    const button = event.currentTarget;
    button.disabled = true;
    await saveNative(item, mode, instance, body, error, modal.close, reload);
    button.disabled = false;
  }, disconnect));
  wireHandshake(scroll, handshake);
}

/** In-app confirm for removing a connection (replaces window.confirm). */
function confirmDisconnect(name) {
  return confirmDialog({
    title: `Disconnect ${name}?`,
    body: 'This removes the stored credential. Bonnie stops reading from this source until you connect it again.',
    confirmLabel: 'Disconnect',
    cancelLabel: 'Keep connected',
  });
}

async function removeNative(instance, item, close, reload) {
  const name = (instance && instance.label) || item.title;
  if (!(await confirmDisconnect(name))) return;
  const result = await api(`/connectors/${encodeURIComponent(instance.id)}`, { method: 'DELETE' });
  if (!result || result.error) { toast((result && result.error) || 'Could not disconnect', 'error'); return; }
  close();
  toast('Disconnected');
  if (reload) reload();
}

function pickInstance(item, opts, reload) {
  const body = el('div', { class: 'cx-book' },
    el('p', { class: 'cx-copy' }, 'Choose which connection to manage.'),
    ...(item.instances || []).map((instance) => cxBtn(
      `${instance.label || item.title} · ${instance.health_status || 'connected'}`,
      { onClick: () => { modal.close(); openNativeForm({ ...item, productId: opts.productId }, 'manage', instance, opts, reload); } },
    )));
  const modal = openModal({ title: `Manage ${item.title}`, body, className: 'cx-modal' });
  titleWithMark(`Manage ${item.title}`, item.name, item.title);
}

function endpointFor(item) {
  const registry = String(item.registry_url || '');
  const source = /^https?:\/\/(github\.com|gitlab\.com|bitbucket\.org)\//.test(registry);
  return item.connection_url || (source ? '' : registry);
}

function mcpFields(item, editing) {
  const auth = item.auth_type || 'bearer';
  const credential = auth === 'none' ? null : textField(
    'credential',
    auth === 'api_key' ? 'API Key' : 'Access Token',
    '',
    editing ? 'Current token stays hidden. Paste a new one to replace it.' : 'Paste your token',
  );
  if (credential) credential.querySelector('input').setAttribute('type', 'password');
  return el('div', { class: 'cx-fields' },
    textField('endpoint', 'Endpoint URL', editing ? endpointFor(item) : endpointFor(item), 'https://…'),
    credential,
    textField('label', 'Label (optional)', '', 'e.g. Production'),
    auth === 'none' ? el('p', { class: 'cx-hint' }, 'No authentication required — this connector is public.') : null,
    auth === 'oauth' ? el('p', { class: 'cx-hint' }, 'Paste a valid access token to connect now.') : null);
}

async function saveMcp(item, root, error, close, reload, editing) {
  const values = readFields(root);
  if (!values.endpoint) { error.textContent = 'Endpoint URL is required'; return; }
  if (!editing && item.auth_type !== 'none' && item.auth_type !== 'oauth' && !values.credential) {
    error.textContent = item.auth_type === 'api_key' ? 'API Key is required' : 'Access Token is required';
    return;
  }
  const payload = {
    server_id: item.id,
    label: values.label || undefined,
    product_id: item.productId || null,
    endpoint_url: values.endpoint,
  };
  if (values.credential) payload.credential = values.credential;
  const result = await api('/mcp/connect', { method: 'POST', body: payload });
  if (result && result.error && !result.connection) { error.textContent = result.error; return; }
  close();
  toast(result && result.connected ? `Connected · ${result.tools || 0} tools` : 'Saved. The handshake did not succeed.');
  if (reload) reload();
}

async function removeMcp(item, close, reload) {
  if (!item.connection_id) return;
  if (!(await confirmDisconnect(item.title))) return;
  const result = await api(`/mcp/connect/${encodeURIComponent(item.connection_id)}`, { method: 'DELETE' });
  if (!result || result.error) { toast((result && result.error) || 'Could not disconnect', 'error'); return; }
  close();
  toast('Disconnected');
  if (reload) reload();
}

function openMcp(item, mode, reload) {
  const editing = mode === 'manage';
  const error = errorLine();
  const fields = mcpFields(item, editing);
  const body = el('div', { class: 'cx-book' },
    writeNote(item),
    el('p', { class: 'cx-hint' }, `MCP · ${String(item.transport || 'http').toUpperCase()} · ${item.meta || ''}`),
    error,
    fields,
    item.description ? el('p', { class: 'cx-copy' }, item.description) : null);
  const title = editing ? `Manage ${item.title}` : `Connect ${item.title}`;
  const modal = openModal({
    title,
    body,
    className: 'cx-modal',
  });
  titleWithMark(title, item.name, item.title);
  const disconnect = editing && item.connection_id
    ? cxBtn('Disconnect', { onClick: () => removeMcp(item, modal.close, reload) })
    : null;
  body.append(submitRow(editing ? 'Save & verify' : 'Connect', async (event) => {
    const button = event.currentTarget;
    button.disabled = true;
    await saveMcp(item, body, error, modal.close, reload, editing);
    button.disabled = false;
  }, disconnect));
}

export function openConnectorDialog(item, mode, opts, reload) {
  const productId = opts && opts.productId;
  if (!productId && mode !== 'manage') {
    toast('Pick a product before connecting.', 'error');
    return;
  }
  if (item.kind === 'mcp') {
    openMcp({ ...item, productId }, mode, reload);
    return;
  }
  if (mode === 'manage' && (item.instances || []).length > 1) {
    pickInstance({ ...item, productId }, opts, reload);
    return;
  }
  const instance = mode === 'manage' ? (item.instances || [])[0] : null;
  if (mode === 'manage' && !instance) { toast('Nothing to manage yet', 'error'); return; }
  openNativeForm({ ...item, productId }, mode, instance, opts, reload);
}
